import { Autocomplete, TextField } from "@mui/material";
import type { FC } from "react";
import { usePackages } from "../../admin/packages/usePackages";
import type { Currency } from "../../data/BaseEntity";

export interface Package {
  readonly id: { id: string; userFriendlyId: string | null };
  readonly name: string;
  readonly tags: { readonly category: string; readonly value: string }[];
  readonly currency: Currency;
  readonly margin: number;
  readonly days: {
    readonly dayNumber: number;
    readonly details: { readonly description?: string; readonly plan?: string };
    readonly products: {
      readonly id: string;
      readonly optional?: boolean;
      readonly options: string[];
    }[];
  }[];
}

interface Props {
  readonly packageId: string | undefined;
  readonly fullWidth?: boolean;
  readonly size?: "small" | "medium";
  readonly disabled?: boolean;
  readonly disableClearable?: boolean;
  readonly onChange: (p: Package | undefined) => void;
  readonly label?: string;
  readonly exceptPackageId?: string;
  readonly placeholder?: string;
  readonly parentOnly?: boolean;
}

const PackageSelect: FC<Props> = ({
  packageId,
  fullWidth,
  size,
  disabled,
  onChange,
  disableClearable,
  label = "Pakiet",
  exceptPackageId,
  parentOnly = false,
}) => {
  const { packages, isLoading } = usePackages();
  const options = (packages ?? [])
    .filter((p) => p.id.id !== exceptPackageId)
    .filter((p) => !parentOnly || !p.parentPackageId)
    .map((p) => ({ id: p.id.id, label: p.name.value }));
  const selectedOption = options.find((o) => o.id === packageId) ?? null;

  const handleChange = (selectedId: string | null) => {
    if (selectedId == null) {
      onChange(undefined);
      return;
    }
    const selectedPackage = packages.find((p) => p.id.id === selectedId);
    onChange(
      selectedPackage
        ? { ...selectedPackage, name: selectedPackage.name.value, tags: selectedPackage.tags ?? [] }
        : undefined,
    );
  };

  return (
    <Autocomplete
      loading={isLoading}
      disablePortal
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      multiple={false}
      size={size}
      disabled={disabled}
      value={selectedOption}
      options={options}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      onChange={(e, selected) => {
        if (selected == null) {
          handleChange(null);
          return;
        }
        const id = typeof selected === "string" ? selected : selected.id;
        handleChange(id);
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};

export default PackageSelect;
